import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PHOTO_PICKER_MODEL } from "../../../components/PhotoPicker/SubComponent/Models/Models";
import { CreateResidentWhiteList, UploadResidentWhiteList } from "../../../reduxToolkit/Building/BuildingApi";
import { setWhiteListImportFile, setWhiteListImportResult } from "../../../reduxToolkit/Building/BuildingSlice";
import routes from "../../../Routes/routes";
import { errorMsg } from "../../../Apis/functions";
import {
    BUILDING_WHITE_LIST_AUTOMATIC_MODEL,
    BUILDING_WHITE_LIST_MANUAL_MODEL,
    BUILDING_WHITE_LIST_MODEL,
    mergeWhiteListSummaries,
    whiteListFileWithoutHeader
} from "../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * White list modal logic: the form model of the manual tab, the file of the
 * automatic one and the validation done before saving.
 *
 * @param {boolean} props.show - Modal visibility, the form is reset every time it changes.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Function} props.onSaved - Notifies the white list tab so it reloads its list.
 * @param {string} props.buildingId - Building the residents are authorized in.
 * @returns {Object} Localization function, form state, the display class of the
 * modal and the modal handlers.
 */
export const AddToWhiteListModalApp = (props) => {
    const { show, onHide, onSaved, buildingId } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [emptyFields, setEmptyFields] = useState(false);
    // Hides this modal while the delete resident one is open
    const [displayModal, setDisplayModal] = useState('');
    const [photoPickerFile, setPhotoPickerFile] = useState({ ...PHOTO_PICKER_MODEL });
    const [whiteListDetail, setWhiteListDetail] = useState({ ...BUILDING_WHITE_LIST_MODEL });

    useEffect(() => {
        reset();
    }, [show]);

    /**
     * Leaves the form as it is opened, one empty email row and no file selected.
     */
    const reset = () => {
        setEmptyFields(false);
        setDisplayModal('');
        setPhotoPickerFile({ ...PHOTO_PICKER_MODEL });
        setWhiteListDetail({
            ...BUILDING_WHITE_LIST_MODEL,
            whiteListManual: [{ ...BUILDING_WHITE_LIST_MANUAL_MODEL, id: `${Date.now()}` }]
        });
    };

    /**
     * Closes the modal and clears the validation flag.
     */
    const handleClose = () => {
        setEmptyFields(false);
        onHide();
    };

    /**
     * Sends the white list of the building: the template of the automatic tab
     * goes to the upload endpoint and the residents captured by hand to the
     * create all one. Both save row by row, the failures of the two are put
     * together and the import errors view is opened with them so they can be
     * corrected and sent again.
     */
    const handleSaveWhiteList = async () => {
        const hasFile = BUILDING_WHITE_LIST_AUTOMATIC_MODEL.isValid({ photoPickerFile });
        const hasManual = whiteListDetail?.hasManualData();

        if (!hasFile && !hasManual) {
            setEmptyFields(true);
            toast.warning(t('please_fill_all_fields'));
            return;
        }

        if (hasManual && !whiteListDetail?.isValidWhiteList()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);

        const summaries = [];
        // The upload endpoint reads every row of the file as a resident, the
        // titles of the columns of the template are taken out before sending it
        // so they are not registered as one more
        const fileSelected = hasFile
            ? await whiteListFileWithoutHeader(photoPickerFile?.fileSelected)
            : null;

        if (hasFile) {
            const { payload: { data: { data, success, code } } } = await dispatch(
                UploadResidentWhiteList(
                    BUILDING_WHITE_LIST_AUTOMATIC_MODEL.toRegister({
                        buildingId,
                        photoPickerFile: { fileSelected }
                    })
                )
            );

            if (!success) {
                errorMsg(code);
                return;
            }

            summaries.push(data);
        }

        if (hasManual) {
            const { payload: { data: { data, success, code } } } = await dispatch(
                CreateResidentWhiteList(whiteListDetail.toRegister(buildingId))
            );

            if (!success) {
                errorMsg(code);
                return;
            }

            summaries.push(data);
        }

        const result = mergeWhiteListSummaries(summaries);

        // Some rows may have been created even when others failed, the list is
        // reloaded either way
        onSaved?.();

        if (result?.failedRecords > 0) {
            dispatch(setWhiteListImportResult(result));
            // The errors view reads back the sheet that was sent to paint the
            // rows the failures point at, only the manual tab travels without a
            // file
            dispatch(setWhiteListImportFile(fileSelected));
            handleClose();
            navigate(routes.building.whiteListImportErrors, { state: { buildingId } });
            return;
        }

        toast.success(t('has_been_created_successfully'));
        handleClose();
    };

    return {
        t,
        emptyFields,
        setEmptyFields,
        displayModal,
        setDisplayModal,
        photoPickerFile,
        setPhotoPickerFile,
        whiteListDetail,
        setWhiteListDetail,
        handleClose,
        handleSaveWhiteList,
    };
};
